// Журнал изменений языком пользователя — общий для обеих систем.
// Сервер уже подписывает записи по-русски (что, поле, было → стало); здесь записи одного действия
// (перенос заявки меняет все её рейсы, сдвиг плана — десятки работ) сворачиваются в одну строку.

import { Fragment, useState } from "react";
import { fmtDateTime } from "./format";

export type AuditRow = {
  at: string; user: string; reason: string | null; entity: string; action: string; object: string;
  field: string | null; old?: string; new?: string;
};

export function groupAudit(rows: AuditRow[]): AuditRow[][] {
  const out: AuditRow[][] = [];
  for (const r of rows) {
    const g = out[out.length - 1];
    const head = g?.[0];
    const near = head && Math.abs(new Date(head.at).getTime() - new Date(r.at).getTime()) < 5000;
    if (g && near && head.user === r.user && (head.reason || "") === (r.reason || "")) g.push(r);
    else out.push([r]);
  }
  return out;
}

export function auditChange(r: AuditRow): string {
  if (r.action === "изменено" && r.field) return `${r.field}: ${r.old ?? "—"} → ${r.new ?? "—"}`;
  if (r.action === "создано") return r.new && r.new !== "—" ? `создано: ${r.new}` : "создано";
  if (r.action === "удалено") return "удалено";
  return r.field || r.action;
}

export function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

/** Таблица журнала. compact — без колонки «Что изменили» для одной записи (история в карточке). */
export function AuditTable({ rows }: { rows: AuditRow[] }) {
  const [open, setOpen] = useState<number | null>(null);
  const groups = groupAudit(rows);
  return (
    <div className="table-wrap">
      <table className="table">
        <thead><tr><th>Когда</th><th>Кто</th><th>Что изменили</th><th>Изменение</th><th>Причина</th></tr></thead>
        <tbody>{groups.map((g, n) => {
          const r = g[0];
          if (g.length === 1) {
            return (
              <tr key={n}>
                <td className="small">{fmtDateTime(r.at)}</td><td className="small">{r.user}</td>
                <td><b>{r.entity}</b>{r.object ? ` · ${r.object}` : ""}</td><td className="small">{auditChange(r)}</td><td className="small">{r.reason || "—"}</td>
              </tr>
            );
          }
          const kinds = [...new Set(g.map((x) => x.entity))].join(", ");
          const objects = [...new Set(g.map((x) => `${x.entity} · ${x.object}`))];
          return (
            <Fragment key={n}>
              <tr className="click" onClick={() => setOpen(open === n ? null : n)}>
                <td className="small">{fmtDateTime(r.at)}</td><td className="small">{r.user}</td>
                <td><b>{kinds}</b> · {objects.length === 1 ? r.object : `${objects.length} ${plural(objects.length, "запись", "записи", "записей")}`}</td>
                <td className="small">{g.length} {plural(g.length, "изменение", "изменения", "изменений")} · <span className="audit-more">{open === n ? "скрыть" : "подробнее"}</span></td>
                <td className="small">{r.reason || "—"}</td>
              </tr>
              {open === n && g.map((x, k) => (
                <tr key={k} className="sub">
                  <td /><td /><td className="small">{x.entity} · {x.object}</td><td className="small">{auditChange(x)}</td><td />
                </tr>
              ))}
            </Fragment>
          );
        })}</tbody>
      </table>
    </div>
  );
}
